<template>
  <article
    class="dining-card"
    :class="[`dining-card-${variant}`, { 'draft-preview': venue.status === 'draft' }]"
    role="link"
    tabindex="0"
    :aria-label="`View details for ${venue.name}`"
    @click="openDetails"
    @keydown.enter="goToDetails"
  >
    <v-img
      v-if="imageSource"
      :src="imageSource | mediaSrc"
      :alt="imageAlt"
      :position="imagePosition"
      class="dining-image"
    >
      <span class="venue-type">{{ venue.type || 'Food & Drink' }}</span>
      <DraftBadge v-if="venue.status === 'draft'" class="draft-card-badge" />
    </v-img>
    <div v-else class="dining-image dining-placeholder">
      <v-icon>mdi-silverware-fork-knife</v-icon>
      <span class="venue-type">{{ venue.type || 'Food & Drink' }}</span>
      <DraftBadge v-if="venue.status === 'draft'" class="draft-card-badge" />
    </div>
    <div class="dining-content">
      <p v-if="location" class="venue-location"><v-icon x-small>mdi-map-marker-outline</v-icon>{{ location }}</p>
      <h3>{{ venue.name }}</h3>
      <p v-if="venue.description" class="venue-summary">{{ venue.description }}</p>
      <dl class="venue-facts">
        <div v-if="visitDate"><dt>Visited</dt><dd>{{ visitDate }}<span v-if="additionalVisitCount"> + {{ additionalVisitCount }} more</span></dd></div>
        <div v-if="showJourney && journey"><dt>Journey</dt><dd>{{ journey }}</dd></div>
      </dl>
      <div class="dining-actions">
        <router-link :to="venue.href" class="details-link" @click.native.stop>
          Details <v-icon x-small>mdi-arrow-right</v-icon>
        </router-link>
        <router-link
          v-if="showStory && venue.hasNarrative && venue.storyHref"
          :to="venue.storyHref"
          class="story-link"
          @click.native.stop
        >
          Story <v-icon x-small>mdi-book-open-page-variant</v-icon>
        </router-link>
      </div>
    </div>
  </article>
</template>

<script>
import DraftBadge from '@/components/draft/DraftBadge.vue';

export default {
  name: 'DiningCard',
  components: { DraftBadge },
  props: {
    venue: { type: Object, required: true },
    variant: { type: String, default: 'archive', validator: (value) => ['archive', 'journey'].includes(value) },
    showJourney: { type: Boolean, default: true },
    showStory: { type: Boolean, default: true },
  },
  computed: {
    imageSource() { return this.venue.image?.src || this.venue.image || ''; },
    imageAlt() { return this.venue.image?.alt || `${this.venue.name} in ${this.location}`; },
    imagePosition() {
      return this.venue.image?.objectPosition
        || this.venue.image?.position
        || this.venue.imagePosition
        || 'center center';
    },
    location() { return this.venue.locations?.[0] || this.venue.location || this.venue.destinations?.[0] || ''; },
    visitDate() { return this.venue.visitDates?.[0] || this.venue.dateLabel || ''; },
    additionalVisitCount() { return Math.max((this.venue.visitDates?.length || 0) - 1, 0); },
    journey() { return this.venue.journeys?.[0] || this.venue.journey || ''; },
  },
  methods: {
    openDetails(event) {
      if (event.target.closest('a,button')) return;
      this.goToDetails();
    },
    goToDetails() { if (this.venue.href) this.$router.push(this.venue.href); },
  },
};
</script>

<style scoped>
.dining-card{background:linear-gradient(145deg,#16130f,#0b0b0b 60%);border:1px solid #332d24;border-radius:12px;color:inherit;cursor:pointer;display:flex;flex-direction:column;min-height:100%;overflow:hidden;transition:border-color 160ms ease,transform 160ms ease}.dining-card:hover,.dining-card:focus-visible{border-color:#e23232;outline:none;transform:translateY(-3px)}.dining-image{border-bottom:1px solid #332d24;flex:0 0 210px;height:210px}.dining-placeholder{align-items:center;background:radial-gradient(circle,#24201a,#101010 70%);display:flex;justify-content:center;position:relative}.dining-placeholder>.v-icon{color:#765f3d;font-size:4rem}.venue-type{background:rgba(5,5,5,.88);border:1px solid rgba(255,255,255,.2);border-radius:999px;color:#fff;font-size:.68rem;font-weight:900;left:12px;padding:6px 9px;position:absolute;text-transform:uppercase;top:12px}.dining-content{display:flex;flex:1;flex-direction:column;padding:20px}.venue-location{align-items:center;color:#e85a5a;display:flex;font-size:.72rem;font-weight:900;gap:5px;margin:0;text-transform:uppercase}.venue-location .v-icon{color:inherit}h3{color:#fff;font-family:"Oswald",sans-serif;font-size:1.55rem;line-height:1.1;margin:9px 0 10px}.venue-summary{-webkit-box-orient:vertical;-webkit-line-clamp:2;color:#c2c2c2;display:-webkit-box;line-height:1.5;margin:0;min-height:3em;overflow:hidden}.venue-facts{border-top:1px solid #302a22;display:grid;gap:8px;margin:16px 0 0;padding-top:13px}.venue-facts div{display:grid;gap:8px;grid-template-columns:58px 1fr}.venue-facts dt{color:#888;font-size:.65rem;font-weight:900;text-transform:uppercase}.venue-facts dd{color:#ddd;font-size:.82rem;margin:0}.dining-actions{display:flex;gap:9px;margin-top:auto;padding-top:18px;position:relative;z-index:2}.dining-actions a{align-items:center;border:1px solid #4a4a4a;border-radius:999px;display:inline-flex;font-size:.72rem;font-weight:900;gap:5px;padding:7px 11px;text-decoration:none;text-transform:uppercase}.details-link{background:#e23232;border-color:#e23232!important;color:#fff}.story-link{color:#f0b13c}.dining-actions .v-icon{color:inherit}.dining-card-journey .dining-image{flex-basis:190px;height:190px}.dining-card-journey .dining-content{padding:18px}@media(max-width:600px){.dining-image,.dining-card-journey .dining-image{flex-basis:200px;height:200px}}
.dining-card.draft-preview{background:linear-gradient(145deg,rgba(240,177,60,.15),#0b0b0b 60%);border-color:rgba(240,177,60,.72)}.draft-card-badge{position:absolute;right:12px;top:12px;z-index:2}
</style>
